import React, { useState, useEffect } from 'react';

import { Modal, Table, Form, Input, InputNumber } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { NotificationInstance } from 'antd/es/notification/interface';
import { LoadingOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';
import Toolbar from '@controls/toolbar/toolbar';

import { exception, securityRestriction } from '@extensions/notification';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { ICargoCarrier } from '@entities/cargo-carrier';

import { Currency } from '@enums/currency';
import { CargoCarrierType } from '@enums/cargo-carrier-type';
import { Permission, hasPermission } from '@enums/permission';

interface ICargoProps extends IBaseComponentProps {
    countryId?: string | undefined;
    api: NotificationInstance;
}

const AirCargoCarriers = (props: ICargoProps) => {
    const { countryId, api, serverFetch } = props;

    const [cargoForm] = Form.useForm();
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [entities, setEntities] = useState<Array<ICargoCarrier>>([]);
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [currentEntity, setCurrentEntity] = useState<ICargoCarrier>();
    const [refreshRequired, setRefreshRequired] = useState<boolean>();
    const [loading, setLoading] = useState<boolean>(false);
    const [cargoOpen, setCargoOpen] = useState<boolean>(false);

    const [isViewOnly] = useState<boolean>(!hasPermission(userSession.permissions, Permission.ManageCountries));

    useEffect(() => {
        setSelectedIds([]);
        setCurrentEntity(undefined);
        cargoForm.resetFields();
        setRefreshRequired(true);
    }, [countryId]);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setEntities([]);

        setLoading(true);

        serverFetch(`cargocarriers/${countryId}/air`, { method: 'GET' })
            .then((data: Array<ICargoCarrier>) => {
                setEntities(data);

                cargoForm.resetFields();
                setCargoOpen(false);

                setLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения авиаперевозчиков', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    const onDelete = () => {
        if (!currentEntity) return;

        serverFetch(`cargocarriers/${currentEntity.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления авиаперевозчика', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSave = (data: ICargoCarrier) => {
        setLoading(true);

        serverFetch(`cargocarriers`, { method: data.id ? 'PUT' : 'POST', bodyData: data })
            .then(() => {
                setLoading(false);
                setRefreshRequired(true);

                cargoForm.resetFields();
                setCargoOpen(false);
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения авиаперевозчика', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить',
                        key: 'add',
                        type: 'primary',
                        icon: <PlusOutlined />,
                        disabled: loading || isViewOnly,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageCargoCarriers)) {
                                securityRestriction(api, [Permission.ManageCargoCarriers]);
                                return;
                            }

                            setSelectedIds([]);
                            setCurrentEntity(undefined);

                            setCargoOpen(true);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentEntity || isViewOnly,
                        icon: <EditOutlined />,
                        onClick: () => {
                            setCargoOpen(true);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentEntity || isViewOnly,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteCargoCarrier)) {
                                securityRestriction(api, [Permission.DeleteCargoCarrier]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить авиаперевозчика "${currentEntity?.name}"?`,
                                okType: 'primary',
                                icon: <QuestionCircleOutlined />,
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = entities.find((o) => o.id == selectedRowKeys[0]);
            setCurrentEntity(entity);
        } else {
            setCurrentEntity(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<ICargoCarrier> = [
            {
                title: 'Название',
                dataIndex: 'name',
                width: 250,
            },
            {
                title: 'Код',
                dataIndex: 'code',
                width: 80,
            },
            {
                title: 'Тариф',
                align: 'center',
                dataIndex: 'price',
                width: 180,
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                }}
                columns={columns}
                dataSource={entities}
                loading={tableLoading}
                onRow={(record: ICargoCarrier) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: `calc(100vh - 175px)` }}
            />
        );
    };

    return (
        <>
            {renderToolbar()}
            {renderTable()}
            <Modal
                destroyOnHidden={true}
                closable={false}
                title={currentEntity ? 'Изменить авиаперевозчика' : 'Добавить авиаперевозчика'}
                open={cargoOpen}
                okText='ОК'
                onOk={() => cargoForm.submit()}
                onCancel={() => {
                    cargoForm.resetFields();
                    setCargoOpen(false);
                }}
                width={500}
            >
                <Form
                    preserve={false}
                    colon={false}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    style={{ marginTop: 20 }}
                    onFinish={onSave}
                    form={cargoForm}
                >
                    <Form.Item hidden name='id' initialValue={currentEntity?.id}>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name='type' initialValue={CargoCarrierType.Air}>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name='currency' initialValue={Currency.Usd}>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name='countryId' initialValue={countryId}>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        initialValue={currentEntity?.name}
                        label='Название'
                        name='name'
                        required={true}
                        rules={[{ required: true, message: 'Укажите название' }]}
                    >
                        <Input autoFocus disabled={isViewOnly} />
                    </Form.Item>
                    <Form.Item
                        initialValue={currentEntity?.code}
                        required
                        label='Код'
                        name='code'
                        rules={[{ required: true, message: 'Укажите код авиаперевозчика' }]}
                    >
                        <Input disabled={isViewOnly} />
                    </Form.Item>
                    <Form.Item label='Тариф' initialValue={currentEntity?.price} name='price'>
                        <InputNumber decimalSeparator=',' min={0} step='0.1' stringMode disabled={isViewOnly} />
                    </Form.Item>
                </Form>
            </Modal>

            {modalContextHolder}
        </>
    );
};

export default AirCargoCarriers;
